import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { toast } from "react-toastify";
import { Check } from "react-feather";
import { pointService } from "../../../services/pointService";
import MdiIconPicker from "../../Common/MdiIconPicker";
/**
 * EditPointModal - Modal for editing point settings on a device.
 * Manages its own local state for the form fields.
 * Props:
 *   isOpen: bool
 *   point: the point being edited (editingPoint)
 *   deviceId: ID of the device the point belongs to
 *   deviceName: display name of the device
 *   onClose: () => void
 *   onSuccess: () => void — called after a successful update to refresh parent
 */
const EditPointModal = ({ isOpen, point, deviceId, deviceName, onClose, onSuccess, pointTypes, loadingPointTypes }) => {
  const [editPointData, setEditPointData] = useState({
    isMainReadPoint: false,
    isMainWritePoint: false,
    displayOrder: "",
    displayName: "",
    icon: "",
    pointTypeId: "",
  });
  const [updatingPoint, setUpdatingPoint] = useState(false);

  useEffect(() => {
    if (isOpen && point) {
      setEditPointData({
        isMainReadPoint: point.isMainReadPoint || false,
        isMainWritePoint: point.isMainWritePoint || false,
        displayOrder: point.displayOrder !== null && point.displayOrder !== undefined ? point.displayOrder : "",
        displayName: point.displayName || "",
        icon: point.icon || "",
        pointTypeId: pointTypes?.find((pt) => pt.enumValue === point.pointTypeEnum)?.id?.toString() || "",
      });
    }
  }, [isOpen, point, pointTypes]);

  const handleEditPointChange = (field, value) => {
    setEditPointData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirmEditPoint = async () => {
    if (!point || !deviceId) return;

    setUpdatingPoint(true);
    try {
      const body = {
        deviceId: deviceId,
        isMainReadPoint: editPointData.isMainReadPoint,
        isMainWritePoint: editPointData.isMainWritePoint,
        displayName: editPointData.displayName?.trim() || undefined,
        icon: editPointData.icon?.trim() || undefined,
        ...(editPointData.pointTypeId && { pointTypeId: parseInt(editPointData.pointTypeId) }),
      };
      body.displayOrder = editPointData.displayOrder !== "" && editPointData.displayOrder !== null
        ? Number(editPointData.displayOrder)
        : null;

      await pointService.assignDevice(point.pointId, body);
      toast.success("Point updated successfully");

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating point:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update point");
      }
    } finally {
      setUpdatingPoint(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Edit Point Settings</ModalHeader>
      <ModalBody>
        <div
          className="p-3 mb-3 rounded"
          style={{ background: "var(--bs-light, #f8f9fa)", border: "1px solid var(--bs-border-color, #dee2e6)" }}
        >
          <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--bs-secondary, #6c757d)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Point Details
          </div>
          <Row>
            <Col md="12">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Point Name</Label>
                <Input bsSize="sm" type="text" value={point?.pointDisplayName || point?.displayName || "-"} disabled />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Device</Label>
                <Input bsSize="sm" type="text" value={deviceName || "-"} disabled />
              </FormGroup>
            </Col>
            <Col md="">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Point ID</Label>
                <Input bsSize="sm" type="text" value={point?.pointId || "-"} disabled style={{ fontFamily: "monospace" }} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="2">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Kind</Label>
                <Input bsSize="sm" type="text" value={point?.kind || "-"} disabled />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Current Value</Label>
                <Input
                  bsSize="sm"
                  type="text"
                  value={
                    point?.curVal !== null && point?.curVal !== undefined
                      ? typeof point.curVal === "object"
                        ? point.curVal.val ?? "-"
                        : point.curVal
                      : "-"
                  }
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Unit</Label>
                <Input
                  bsSize="sm"
                  type="text"
                  value={(typeof point?.curVal === "object" && point?.curVal?.unit) || point?.unit || "-"}
                  disabled
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-2">
                <Label style={{ fontSize: "0.8rem", marginBottom: "2px" }}>Writable</Label>
                <Input bsSize="sm" type="text" value={point?.writable ? "Yes" : "No"} disabled />
              </FormGroup>
            </Col>
          </Row>
        </div>
        <hr />

        <div className="mb-3">
          <div className="checkbox">
            <Input
              id="isMainReadPoint"
              type="checkbox"
              checked={editPointData.isMainReadPoint}
              onChange={(e) => handleEditPointChange("isMainReadPoint", e.target.checked)}
              disabled={updatingPoint}
            />
            <Label htmlFor="isMainReadPoint">Is Main Read Point</Label>
          </div>
          <small className="d-block text-muted" style={{ marginLeft: "1.5rem" }}>
            Mark this as the primary point for reading values from the device
          </small>
        </div>

        <div className="mb-3">
          <div className="checkbox">
            <Input
              id="isMainWritePoint"
              type="checkbox"
              checked={editPointData.isMainWritePoint}
              onChange={(e) => handleEditPointChange("isMainWritePoint", e.target.checked)}
              disabled={updatingPoint}
            />
            <Label htmlFor="isMainWritePoint">Is Main Write Point</Label>
          </div>
          <small className="d-block text-muted" style={{ marginLeft: "1.5rem" }}>
            Mark this as the primary point for writing values to the device
          </small>
        </div>

        <FormGroup>
          <Label for="editPointDisplayOrder">Display Order</Label>
          <Input
            id="editPointDisplayOrder"
            type="number"
            placeholder="e.g., 1, 2, 3"
            value={editPointData.displayOrder}
            onChange={(e) => handleEditPointChange("displayOrder", e.target.value)}
            disabled={updatingPoint}
          />
          <small className="text-muted">
            Order in which this point should be displayed (optional)
          </small>
        </FormGroup>

        <FormGroup>
          <Label for="editPointDisplayName">Display Name</Label>
          <Input
            id="editPointDisplayName"
            type="text"
            placeholder="Enter display name for this point..."
            value={editPointData.displayName}
            onChange={(e) => handleEditPointChange("displayName", e.target.value)}
            disabled={updatingPoint}
          />
          <small className="text-muted">Custom display name for this point on the device</small>
        </FormGroup>

        <FormGroup>
          <Label>Icon</Label>
          <MdiIconPicker
            value={editPointData.icon}
            onChange={(val) => handleEditPointChange("icon", val)}
            disabled={updatingPoint}
          />
          <small className="text-muted">Search from Material Design Icons (MDI) library</small>
        </FormGroup>

        <FormGroup>
          <Label for="editPointType">
            Point Type <span className="text-danger">*</span>
          </Label>
          <Input
              id="editPointType"
              type="select"
              value={editPointData.pointTypeId}
              onChange={((e) => handleEditPointChange("pointTypeId", e.target.value))}
              disabled={updatingPoint || loadingPointTypes}
              invalid={!editPointData.pointTypeId}
          >
            <option value="" disabled>
              {loadingPointTypes ? "Loading point types..." : "— Select point type (required) —"}
            </option>
            {pointTypes?.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}{pt.description ? ` — ${pt.description}` : ""}
                </option>
            ))}
          </Input>
        </FormGroup>

      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={updatingPoint}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirmEditPoint} disabled={updatingPoint}>
          {updatingPoint ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Updating...
            </>
          ) : (
            <>
              <Check size={14} className="me-1" />
              Update Point
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditPointModal;
